import { DateTime } from 'luxon';
import { Instance, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import { getScheduleLesson, saveScheduleLesson } from '@common/api/scheduleLessons';
import { removeScheduleLesson } from '@common/api/scheduleSettings';
import scheduleLessonTypes from '@common/constants/ScheduleLessonTypes';
import Lesson from '@common/models/Lessons/Lesson';
import { IServiceModel } from '@common/models/Services/Service';

import OneCRate from '../OneCRates/OneCRate';
import Teacher from '../Users/Teachers/Teacher';
import Homework from './Homework';
import { IScheduleLessonsModel } from './ScheduleLessons';

const ScheduleLesson = types
	.model('ScheduleLesson', {
		id: types.identifierNumber,
		name: types.maybeNull(types.optional(types.string, '')),
		scheduleProgram: types.maybeNull(types.frozen()),
		lesson: types.maybeNull(Lesson),
		start: types.maybeNull(types.string),
		type: types.maybeNull(types.string),
		duration: types.maybeNull(types.number),
		deadline: types.maybeNull(types.string),
		order_id: types.maybeNull(types.number),
		teacher_id: types.maybeNull(types.number),
		schedule_program_id: types.maybeNull(types.number),
		schedule_id: types.maybeNull(types.number),
		teacher: types.maybeNull(Teacher),
		fromHomework: types.maybeNull(Homework),
		toHomework: types.maybeNull(Homework),
		onecRate: types.maybeNull(OneCRate),
	})
	.volatile(() => ({
		loading: false,
		number: 0,
	}))
	.views((self) => ({
		get dateTime() {
			return DateTime.fromSQL(self.start);
		},
		get dateTimeStart() {
			return DateTime.fromSQL(self.start);
		},
		get dateTimeEnd() {
			return DateTime.fromSQL(self.start).plus({ minutes: self.duration });
		},
	}))
	.views((self) => ({
		get dateString() {
			return self.dateTime.toISODate();
		},
		get timeString() {
			return self.dateTimeStart.toLocaleString(DateTime.TIME_24_SIMPLE);
		},
		get timesString() {
			return `${self.dateTimeStart.toLocaleString(DateTime.TIME_24_SIMPLE)} - ${self.dateTimeEnd.toLocaleString(DateTime.TIME_24_SIMPLE)}`;
		},
		getZoomLink(service: IServiceModel) {
			const zoomLink: Record<string, string | null> = {
				Academy: self.scheduleProgram?.zoom_link,
				Lyceum: self.teacher.zoom_on_off && self.teacher?.zoom_link,
				TIS:
					(self.type === scheduleLessonTypes.Life || self.type === scheduleLessonTypes.Webinar) &&
					self.scheduleProgram?.zoom_link,
			};

			return zoomLink[service?.name] || null;
		},
	}))
	.actions((self) => ({
		fetch: flow(function* () {
			self.loading = true;
			try {
				const result = yield getScheduleLesson(self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		remove() {
			const parent = getParent<IScheduleLessonsModel>(self, 2);
			parent.removeItem(self as IScheduleLessonModel);
		},
		save: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield saveScheduleLesson(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		delete: flow(function* (data: any) {
			try {
				self.loading = true;
				const parent: any = getParent(self, 2);
				yield removeScheduleLesson(self.id, data);
				yield parent.removeLesson(self);
			} catch (e) {
				self.loading = false;
				throw e;
			}
		}),
	}));

export default ScheduleLesson;

export interface IScheduleLessonModel extends Instance<typeof ScheduleLesson> {}
